import { combineReducers, configureStore } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'
import sessionReducer from './session'
// import personReducer from './person'
import person2Reducer from './person/index.js'
import employmentReducer from './employment'
import academicYearReducer from './settings/academic-year'
import markingPeriodReducer from './settings/marking-period'
import academicYearEnrollmentReducer from './academic-year-enrollment'
import courseSessionPeriodReducer from './scheduling/course-session-period'
import courseSessionEnrollmentReducer from './course-session-enrollment'
import lookupReducer, { lookupInitialState } from './lookup'
import academicReducer from './academic'
import settingsReducer from './settings'
import personAttendanceDailyReducer from './person-attendance-daily'
import personAttendancePeriodReducer from './person-attendance-period'
import personGradeReducer from './person-grade'
import memberReducer from './member'
import providerReducer from './provider'
import reportReducer from './report'
import nurseReducer from './nurse'
import incidentReducer from './discipline'
import leaveReducer from './leave'
import assetReducer from './asset'
import adminEvaluationReducer from './evaluation'
import substitutePortalReducer from './substitute'
import applicationReducer from './application'
import schedulingReducer from './scheduling'
import attendanceReducer from './attendance'
import teacherReducer from './teacher'
import hrReducer from './hr'
import mentorshipReducer from './mentorship'
import personLookupReducer from './person/lookup'
import visitorLogReducer from './visitor-log'
import widgetReducer from './widget'
import notificationReducer from './notification'
import needsAttentionReducer from './needs-attention'

import { error as errorSnackbar } from '../utils/snackbar'

const PRESERVED_ON_RESET = {
  lookup: ['types', 'clients', 'serviceCampus'],
  mentorship: null,
  academic: ['courseSession', 'student', 'parentStudent']
}

const sliceInitialStates = { lookup: lookupInitialState }

const preserve = src =>
  Object.fromEntries(
    Object.entries(PRESERVED_ON_RESET).map(([slice, keys]) => [
      slice,
      keys
        ? { ...sliceInitialStates[slice], ...Object.fromEntries(keys.map(k => [k, src?.[slice]?.[k]])) }
        : src?.[slice],
    ])
  )

const combinedReducer = combineReducers({
  session: sessionReducer,
  // person: personReducer,
  employment: employmentReducer,
  academicYear: academicYearReducer,
  markingPeriod: markingPeriodReducer,
  academicYearEnrollment: academicYearEnrollmentReducer,
  courseSessionPeriod: courseSessionPeriodReducer,
  courseSessionEnrollment: courseSessionEnrollmentReducer,
  lookup: lookupReducer,
  academic: academicReducer,
  settings: settingsReducer,
  personAttendanceDaily: personAttendanceDailyReducer,
  personAttendancePeriod: personAttendancePeriodReducer,
  personGrade: personGradeReducer,
  member: memberReducer,
  person2: person2Reducer,
  provider: providerReducer,
  report: reportReducer,
  nurse: nurseReducer,
  discipline: incidentReducer,
  leave: leaveReducer,
  asset: assetReducer,
  adminEvaluation: adminEvaluationReducer,
  substitutePortal: substitutePortalReducer,
  application: applicationReducer,
  scheduling: schedulingReducer,
  attendance: attendanceReducer,
  teacher: teacherReducer,
  hr: hrReducer,
  mentorship: mentorshipReducer,
  personLookup: personLookupReducer,
  visitorLog: visitorLogReducer,
  widget: widgetReducer,
  notification: notificationReducer,
  needsAttention: needsAttentionReducer,
})

const rootReducer = (state, action) => {
  const appearance = state?.session?.appearance ?? localStorage.getItem('appearance') ?? ''
  let discardedState = false

  if (action.error?.status === 401) {
    state = undefined
    discardedState = true
    Cookies.remove('sessionid')
  }
  if (action.error) {
    const silentTypes = [
      'auth/login/rejected',
      'application/save/rejected',
      'academic/parent/address-change/options/rejected',
      'needs-attention/address-change/search/rejected',
    ]
    if (!silentTypes.includes(action.type)) errorSnackbar(action.error?.data?.message || action.error?.data?.error || action.error?.message)
  }
  if (['session/logout'].includes(action.type)) {
    state = undefined
    discardedState = true
  } else if (
    [
      'session/reload',
      'session/register/fulfilled',
      'session/impersonate/fulfilled',
      'session/set-employment/fulfilled',
      'session/set-enrollment/fulfilled',
      'session/set-school/fulfilled',
      'session/set-academicyear/fulfilled',
      'session/set-student/fulfilled',
    ].includes(action.type)
  ) {
    state = { session: { initialized: true, appearance }, ...preserve(state) }
  }

  const next = combinedReducer(state, action)

  return discardedState ? { ...next, session: { ...next.session, appearance } } : next
}

const store = configureStore({
  reducer: rootReducer,
})

export const hasPermission = requires => {
  const { session } = store.getState()
  const args = Array.isArray(requires) ? requires : [requires]
  return !!args.filter(Boolean).reduce((aggr, arg) => {
    if (!aggr) return aggr
    if (typeof arg === 'function') return arg(session)
    return session.permissions[arg]
  }, true)
}

export function hasFullPermission(key) {
  const perms = store.getState().session?.permissions || {};
  return perms[key] === 15;
}

export default store
